import { type ProcessDoc } from '../rxdb/collections/processes';
import { type ProcessItemSummaryDoc } from '../rxdb/collections/processItemSummaries';
import { useDatabase } from '../rxdb/provider';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';

export type ProcessView = ProcessDoc & {
  commentsCount: number;
  incompleteTodosCount: number;
  lastActivityAt: Date;
};

type ProcessMode = 'active' | 'snoozed';

const isSnoozed = (process: ProcessDoc) =>
  Boolean(process.snoozedUntil && dayjs().isBefore(process.snoozedUntil));

const toProcessView = (
  process: ProcessDoc,
  summary: ProcessItemSummaryDoc | undefined,
): ProcessView => ({
  ...process,
  commentsCount: summary?.commentCount ?? 0,
  incompleteTodosCount: summary?.incompleteTodoCount ?? 0,
  lastActivityAt: new Date(summary?.lastActivityAt ?? process.createdAt),
});

const sortProcessViews = (a: ProcessView, b: ProcessView) => {
  if (a.isPinned !== b.isPinned) {
    return a.isPinned ? -1 : 1;
  }

  return a.lastActivityAt.getTime() - b.lastActivityAt.getTime();
};

export const useProcessViews = ({
  mode,
  workspaceId,
}: {
  mode: ProcessMode;
  workspaceId?: null | string;
}) => {
  const { db } = useDatabase();
  const [processes, setProcesses] = useState<ProcessDoc[]>([]);
  const [summaries, setSummaries] = useState<ProcessItemSummaryDoc[]>([]);

  useEffect(() => {
    const sub = db.processes.find().$.subscribe((docs) => {
      setProcesses(docs.map((doc) => doc.toJSON()));
    });

    return () => sub.unsubscribe();
  }, [db]);

  useEffect(() => {
    const sub = db.processItemSummaries.find().$.subscribe((docs) => {
      setSummaries(docs.map((doc) => doc.toJSON()));
    });

    return () => sub.unsubscribe();
  }, [db]);

  return useMemo(() => {
    const summaryByProcessId = new Map(
      summaries.map((summary) => [summary.processId, summary]),
    );

    return processes
      .filter((process) => process.workspaceId === workspaceId)
      .filter((process) => process.archivedAt === null)
      .filter((process) =>
        mode === 'snoozed' ? isSnoozed(process) : !isSnoozed(process),
      )
      .map((process) =>
        toProcessView(process, summaryByProcessId.get(process.id)),
      )
      .toSorted(sortProcessViews);
  }, [mode, processes, summaries, workspaceId]);
};
